import React, { useCallback, useEffect, useState } from 'react';
import { Film, Link2, Loader2, Plus } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { VideoLinkCard } from './VideoLinkCard';
import {
  getLinkedFromLabel,
  getProviderConfig,
  getProviderLabel,
  getVideoLink,
  hasVideoLink,
  VIDEO_LINK_HINT,
} from '../../utils/videoLink';

function EditPreviewStrip({ preview }) {
  if (!preview?.video_url) return null;
  const provider = preview.video_provider || 'external';
  const cfg = getProviderConfig(provider);
  const linkedFrom = getLinkedFromLabel(provider, preview);
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_99%)]/80 px-3 py-2.5"
      data-testid="video-link-edit-preview"
    >
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[10px] font-semibold ${cfg.markClass}`}
        aria-hidden
      >
        {cfg.mark}
      </span>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-foreground/85">
          {preview.video_provider_label || getProviderLabel(provider)}
        </p>
        <p className="truncate text-[11px] text-muted-foreground">{linkedFrom}</p>
      </div>
    </div>
  );
}

/**
 * Attach / edit / remove an external video link on a story or person profile.
 * View mode: single clean card. Edit mode: form fields only.
 */
export function VideoLinkAttach({
  entity,
  entityLabel = 'memory',
  canEdit = false,
  apiBase,
  putPath,
  deletePath,
  onUpdated,
  className,
  sectionTitle = 'Video memory',
}) {
  const { toast } = useToast();
  const existing = getVideoLink(entity);
  const linked = hasVideoLink(entity);

  const [url, setUrl] = useState('');
  const [title, setTitle] = useState(existing?.video_title || '');
  const [notes, setNotes] = useState(existing?.video_notes || '');
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [allowExternal, setAllowExternal] = useState(true);

  useEffect(() => {
    const link = getVideoLink(entity);
    setTitle(link?.video_title || '');
    setNotes(link?.video_notes || '');
    if (link?.video_url) {
      setEditing(false);
      setUrl('');
      setPreview(null);
    }
  }, [entity]);

  const startEditing = useCallback(() => {
    const link = getVideoLink(entity);
    setEditing(true);
    setUrl(link?.video_url || '');
    setTitle(link?.video_title || '');
    setNotes(link?.video_notes || '');
    setPreview(null);
  }, [entity]);

  const parseUrl = useCallback(async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast({ title: 'Paste a video link', description: VIDEO_LINK_HINT, variant: 'destructive' });
      return null;
    }
    setParsing(true);
    try {
      const { data } = await axios.post(`${apiBase}/video-links/parse`, {
        video_url: trimmed,
        allow_external: allowExternal,
      });
      setPreview(data.preview);
      return data.preview;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || VIDEO_LINK_HINT;
      toast({ title: 'Could not use that link', description: detail, variant: 'destructive' });
      setPreview(null);
      return null;
    } finally {
      setParsing(false);
    }
  }, [url, allowExternal, apiBase, toast]);

  const handleSave = async () => {
    const trimmed = url.trim() || existing?.video_url;
    if (!trimmed) {
      toast({ title: 'Paste a video link', description: VIDEO_LINK_HINT, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (url.trim() && url.trim() !== existing?.video_url) {
        const previewData = await parseUrl();
        if (!previewData && !allowExternal) {
          setSaving(false);
          return;
        }
      }
      const { data } = await axios.put(`${apiBase}${putPath}`, {
        video_url: trimmed,
        video_title: title.trim() || null,
        video_notes: notes.trim() || null,
        allow_external: allowExternal,
      });
      toast({ title: 'Video memory linked' });
      setUrl('');
      setPreview(null);
      setEditing(false);
      onUpdated?.(
        data.video_link
          ? { ...entity, video_link: data.video_link, video_url: data.video_link.video_url }
          : entity
      );
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Could not save video link';
      toast({ title: 'Save failed', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!deletePath) return;
    setSaving(true);
    try {
      await axios.delete(`${apiBase}${deletePath}`);
      toast({ title: 'Video link removed' });
      setUrl('');
      setTitle('');
      setNotes('');
      setPreview(null);
      setEditing(true);
      onUpdated?.({ ...entity, video_link: null, video_url: null });
    } catch (err) {
      toast({ title: 'Remove failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const cancelEditing = () => {
    if (linked) {
      setEditing(false);
      setUrl('');
      setPreview(null);
      setTitle(existing?.video_title || '');
      setNotes(existing?.video_notes || '');
    } else {
      setEditing(false);
      setUrl('');
      setPreview(null);
    }
  };

  if (!canEdit && !linked) return null;

  return (
    <div className={className} data-testid="video-link-attach-panel">
      {linked && !editing ? (
        <VideoLinkCard
          entity={entity}
          canEdit={canEdit}
          onEdit={canEdit ? startEditing : undefined}
          onRemove={canEdit ? handleRemove : undefined}
        />
      ) : canEdit && editing ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Film className="h-4 w-4 text-[hsl(var(--studio-accent,38_36%_42%))]" aria-hidden />
            <p className="font-serif text-base text-foreground/90">{linked ? 'Edit video link' : sectionTitle}</p>
          </div>

          <div className="space-y-3 rounded-xl border border-border/40 bg-muted/10 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="video-url-input" className="text-xs text-muted-foreground">
                Video URL
              </Label>
              <div className="flex gap-2">
                <Input
                  id="video-url-input"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setPreview(null);
                  }}
                  placeholder="https://www.facebook.com/… or YouTube, TikTok, Instagram…"
                  className="h-9 rounded-lg bg-background"
                  data-testid="video-link-url-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 rounded-lg"
                  disabled={parsing || !url.trim()}
                  onClick={parseUrl}
                >
                  {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                  <span className="sr-only">Check link</span>
                </Button>
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">{VIDEO_LINK_HINT}</p>
            </div>

            {preview ? <EditPreviewStrip preview={preview} /> : null}

            <div className="grid gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="video-title-input" className="text-xs text-muted-foreground">
                  Title (optional)
                </Label>
                <Input
                  id="video-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Name this video memory"
                  className="h-9 rounded-lg bg-background font-serif"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="video-notes-input" className="text-xs text-muted-foreground">
                  Caption (optional)
                </Label>
                <Textarea
                  id="video-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="A few words about this video"
                  className="rounded-lg bg-background text-sm font-serif"
                />
              </div>
            </div>

            <label className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={allowExternal}
                onChange={(e) => setAllowExternal(e.target.checked)}
              />
              Also accept other video URLs (generic external link)
            </label>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                className="rounded-full"
                disabled={saving || (!url.trim() && !existing?.video_url)}
                onClick={handleSave}
                data-testid="video-link-save"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save video memory
              </Button>
              <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={cancelEditing}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : canEdit ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Film className="h-4 w-4 text-[hsl(var(--studio-accent,38_36%_42%))]" aria-hidden />
            <p className="font-serif text-base text-foreground/90">{sectionTitle}</p>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Link a video from YouTube, Facebook, Instagram, TikTok, Vimeo, Loom, or elsewhere — it opens on the
            original site for this {entityLabel}.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-[#8B7355]/30 text-[#5c4a32] hover:bg-[#8B7355]/8"
            onClick={startEditing}
            data-testid="video-link-add-btn"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add video link
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default VideoLinkAttach;
